import * as M from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';

import {
  AI_AGENT_MANUAL_UPLOAD_CHANNELS,
  manualUploadChannelOptions,
} from '../../../../../../../shared/constants/aiAgentOptions';
import { AI_AGENT_HISTORY_TYPES, manualHistoryTypeOptions } from '../../../../../../../shared/constants/typeOptions';

import { InfoField } from '../../../../../../ui/infoField/InfoField';
import CustomSearch from '../../../../../../ui/search/customSearch';

import type { KeyboardEvent, MouseEvent, RefObject } from 'react';
import type { IAiAgentManualHistoryFilterForm } from '../type';

interface IAiAgentManualFilterSectionProps {
  filterForm: IAiAgentManualHistoryFilterForm;
  searchType: string;
  inputRef: RefObject<HTMLInputElement>;
  onChangeFilterForm: (nextFilter: Partial<IAiAgentManualHistoryFilterForm>) => void;
  onChangeSearchType: (value: string | null) => void;
  handleSearch: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => void;
  resetSearchInput: () => void;
  onReset: () => void;
}

export default function AiAgentManualFilterSection(props: IAiAgentManualFilterSectionProps) {
  const { filterForm, searchType, inputRef, onChangeFilterForm, onChangeSearchType, handleSearch, resetSearchInput, onReset } =
    props;

  const handleServiceChannel = (
    value: IAiAgentManualHistoryFilterForm['serviceChannels'][number],
    checked: boolean,
  ) => {
    const serviceChannels = checked
      ? Array.from(new Set([...filterForm.serviceChannels, value]))
      : filterForm.serviceChannels.filter((serviceChannel) => serviceChannel !== value);

    onChangeFilterForm({ serviceChannels });
  };

  const handleHistoryType = (value: IAiAgentManualHistoryFilterForm['types'][number], checked: boolean) => {
    const types = checked
      ? Array.from(new Set([...filterForm.types, value]))
      : filterForm.types.filter((historyType) => historyType !== value);

    onChangeFilterForm({ types });
  };

  return (
    <M.Box
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        borderLeft: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <M.SimpleGrid cols={2} spacing={0} verticalSpacing={0} style={{ gridTemplateColumns: '180px minmax(0, 1fr)' }}>
        <InfoField label="기간" labelAlign="left" contentAlign="left">
          <M.Flex gap="xs" align="center" wrap="wrap">
            <DatePickerInput
              leftSection={<IconCalendar size={14} />}
              placeholder="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              w={210}
              value={filterForm.startDate}
              onChange={(date) => onChangeFilterForm({ startDate: date })}
            />
            <M.Text c="gray.7">-</M.Text>
            <DatePickerInput
              leftSection={<IconCalendar size={14} />}
              placeholder="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              w={210}
              value={filterForm.endDate}
              onChange={(date) => onChangeFilterForm({ endDate: date })}
            />
          </M.Flex>
        </InfoField>
        <InfoField label="등록 서비스" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" wrap="wrap">
            <M.Checkbox
              label="전체"
              size="sm"
              checked={filterForm.serviceChannels.length === 0}
              onChange={() => onChangeFilterForm({ serviceChannels: [] })}
            />
            {manualUploadChannelOptions.map((option) => {
              if (option.value === AI_AGENT_MANUAL_UPLOAD_CHANNELS.ALL) {
                return null;
              }

              return (
                <M.Checkbox
                  key={option.value}
                  label={option.label}
                  size="sm"
                  checked={filterForm.serviceChannels.includes(option.value)}
                  onChange={(event) => handleServiceChannel(option.value, event.currentTarget.checked)}
                />
              );
            })}
          </M.Flex>
        </InfoField>
        <InfoField label="구분" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" wrap="wrap">
            <M.Checkbox
              label="전체"
              size="sm"
              checked={filterForm.types.length === 0}
              onChange={() => onChangeFilterForm({ types: [] })}
            />
            {manualHistoryTypeOptions.map((option) => {
              if (option.value === AI_AGENT_HISTORY_TYPES.ALL) {
                return null;
              }

              return (
                <M.Checkbox
                  key={option.value}
                  label={option.label}
                  size="sm"
                  checked={filterForm.types.includes(option.value)}
                  onChange={(event) => handleHistoryType(option.value, event.currentTarget.checked)}
                />
              );
            })}
          </M.Flex>
        </InfoField>
        <InfoField label="상세 검색" labelAlign="left" contentAlign="left">
          <M.Flex gap="sm" align="center" wrap="wrap" w="100%">
            <CustomSearch
              resetSearch={onReset}
              resetSearchInput={resetSearchInput}
              searchType={searchType}
              setSearchType={onChangeSearchType}
              inputRef={inputRef}
              handleSearch={handleSearch}
              searchOptions={[
                {
                  value: 'regId',
                  label: '수정 담당자',
                },
              ]}
              placeholder="검색어를 입력하세요."
            />
          </M.Flex>
        </InfoField>
      </M.SimpleGrid>
    </M.Box>
  );
}
